import { asbError } from '@project/common/util/log';
import type { CopySubtitleMessage, SubtitleModel } from '@project/common';
import { PostMineAction } from '@project/common';
import type { DefaultKeyBinder, KeyBinder, ShortcutEvent } from '@project/common/key-binder';
import type { SeekableTracks, TokenJumpTarget, TokenStatus } from '@project/common/settings';
import type { ExtensionMessage } from '@project/common/app/services/chrome-extension';
import type ChromeExtension from '@project/common/app/services/chrome-extension';

export default class AppKeyBinder implements KeyBinder {
    private readonly defaultKeyBinder: DefaultKeyBinder;
    private readonly extension: ChromeExtension;
    private readonly copyHandlers: ((event: ShortcutEvent) => void)[] = [];
    private readonly mineNewJitenWordsHandlers: ((event: ShortcutEvent) => void)[] = [];
    private readonly ankiExportHandlers: ((event: ShortcutEvent) => void)[] = [];
    private readonly updateLastCardHandlers: ((event: ShortcutEvent) => void)[] = [];
    private readonly takeScreenshotHandlers: ((event: ShortcutEvent) => void)[] = [];
    private readonly toggleRecordingHandlers: ((event: ShortcutEvent) => void)[] = [];
    private readonly selectSubtitleTrackHandlers: ((event: ShortcutEvent) => void)[] = [];
    private _unsubscribeExtension?: () => void;

    constructor(keyBinder: DefaultKeyBinder, extension: ChromeExtension) {
        this.defaultKeyBinder = keyBinder;
        this.extension = extension;

        if (this.extension.installed) {
            const listener = (message: ExtensionMessage) => {
                let handlers: ((event: ShortcutEvent) => void)[] | undefined;
                if (message.data.command === 'copy-subtitle') {
                    const command = message.data as CopySubtitleMessage;

                    switch (command.postMineAction) {
                        case PostMineAction.mineNewJitenWords:
                            handlers = this.mineNewJitenWordsHandlers;
                            break;
                        case PostMineAction.none:
                            handlers = this.copyHandlers;
                            break;
                        case PostMineAction.showAnkiDialog:
                            handlers = this.ankiExportHandlers;
                            break;
                        case PostMineAction.updateLastCard:
                            handlers = this.updateLastCardHandlers;
                            break;
                        default:
                            asbError('app/messages', 'Unknown post mine action ' + command.postMineAction);
                    }
                } else if (message.data.command === 'take-screenshot') {
                    handlers = this.takeScreenshotHandlers;
                } else if (message.data.command === 'toggle-recording') {
                    handlers = this.toggleRecordingHandlers;
                } else if (message.data.command === 'toggle-video-select') {
                    handlers = this.selectSubtitleTrackHandlers;
                }

                if (handlers !== undefined) {
                    for (const h of handlers) {
                        h(new KeyboardEvent('mock'));
                    }
                }
            };
            this._unsubscribeExtension = extension.subscribe(listener);
        }
    }

    bindMineNewJitenWords(onMine: (event: ShortcutEvent) => void, disabledGetter: () => boolean) {
        const handler = (event: ShortcutEvent) => {
            if (!disabledGetter()) onMine(event);
        };
        this.mineNewJitenWordsHandlers.push(handler);
        return () => this._remove(handler, this.mineNewJitenWordsHandlers);
    }

    bindCopy<T extends SubtitleModel = SubtitleModel>(
        onCopy: (event: ShortcutEvent, subtitle: T) => void,
        disabledGetter: () => boolean,
        subtitleGetter: () => T | undefined,
        useCapture?: boolean | undefined
    ): () => void {
        if (this.extension.installed) {
            const handler = this.defaultKeyBinder.copyHandler(onCopy, disabledGetter, subtitleGetter);
            this.copyHandlers.push(handler);
            return () => {
                this._remove(handler, this.copyHandlers);
            };
        }

        return this.defaultKeyBinder.bindCopy(onCopy, disabledGetter, subtitleGetter, useCapture);
    }

    bindAnkiExport(
        onAnkiExport: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        if (this.extension.installed) {
            const handler = this.defaultKeyBinder.ankiExportHandler(onAnkiExport, disabledGetter);
            this.ankiExportHandlers.push(handler);
            return () => {
                this._remove(handler, this.ankiExportHandlers);
            };
        }

        return this.defaultKeyBinder.bindAnkiExport(onAnkiExport, disabledGetter, useCapture);
    }

    bindUpdateLastCard(
        onUpdateLastCard: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        if (this.extension.installed) {
            const handler = this.defaultKeyBinder.updateLastCardHandler(onUpdateLastCard, disabledGetter);
            this.updateLastCardHandlers.push(handler);
            return () => {
                this._remove(handler, this.updateLastCardHandlers);
            };
        }

        return this.defaultKeyBinder.bindUpdateLastCard(onUpdateLastCard, disabledGetter, useCapture);
    }

    bindTakeScreenshot(
        onTakeScreenshot: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        if (this.extension.installed) {
            const handler = this.defaultKeyBinder.takeScreenshotHandler(onTakeScreenshot, disabledGetter);
            this.takeScreenshotHandlers.push(handler);
            return () => {
                this._remove(handler, this.takeScreenshotHandlers);
            };
        }

        return this.defaultKeyBinder.bindTakeScreenshot(onTakeScreenshot, disabledGetter, useCapture);
    }

    bindToggleRecording(
        onToggleRecording: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        if (this.extension.installed) {
            const handler = this.defaultKeyBinder.toggleRecordingHandler(onToggleRecording, disabledGetter);
            this.toggleRecordingHandlers.push(handler);
            return () => {
                this._remove(handler, this.toggleRecordingHandlers);
            };
        }

        return this.defaultKeyBinder.bindToggleRecording(onToggleRecording, disabledGetter, useCapture);
    }

    bindSelectSubtitleTrack(
        onSelectSubtitleTrack: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        if (this.extension.installed) {
            const handler = this.defaultKeyBinder.selectSubtitleTrackHandler(onSelectSubtitleTrack, disabledGetter);
            this.selectSubtitleTrackHandlers.push(handler);
            return () => {
                this._remove(handler, this.selectSubtitleTrackHandlers);
            };
        }

        return this.defaultKeyBinder.bindSelectSubtitleTrack(onSelectSubtitleTrack, disabledGetter, useCapture);
    }

    private _remove(callback: (event: ShortcutEvent) => void, list: ((event: ShortcutEvent) => void)[]) {
        for (let i = list.length - 1; i >= 0; --i) {
            if (callback === list[i]) {
                list.splice(i, 1);
                break;
            }
        }
    }

    bindSeekToSubtitle(
        onSeekToSubtitle: (event: ShortcutEvent, subtitle: SubtitleModel) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindSeekToSubtitle(
            onSeekToSubtitle,
            disabledGetter,
            timeGetter,
            subtitlesGetter,
            seekableTracksGetter,
            useCapture
        );
    }

    bindSeekToBeginningOfCurrentSubtitle(
        onSeekToBeginningOfCurrentSubtitle: (event: ShortcutEvent, subtitle: SubtitleModel) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindSeekToBeginningOfCurrentSubtitle(
            onSeekToBeginningOfCurrentSubtitle,
            disabledGetter,
            timeGetter,
            subtitlesGetter,
            seekableTracksGetter,
            useCapture
        );
    }

    bindSeekBackwardOrForward(
        onSeekBackwardOrForward: (event: ShortcutEvent, forward: boolean) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindSeekBackwardOrForward(onSeekBackwardOrForward, disabledGetter, useCapture);
    }

    bindOffsetToSubtitle(
        onOffsetChange: (event: ShortcutEvent, newOffset: number) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindOffsetToSubtitle(
            onOffsetChange,
            disabledGetter,
            timeGetter,
            subtitlesGetter,
            seekableTracksGetter,
            useCapture
        );
    }

    bindAdjustOffset(
        onOffsetChange: (event: ShortcutEvent, newOffset: number) => void,
        disabledGetter: () => boolean,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindAdjustOffset(onOffsetChange, disabledGetter, subtitlesGetter, useCapture);
    }

    bindResetOffet(
        onResetOffset: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindResetOffet(onResetOffset, disabledGetter, useCapture);
    }

    bindAdjustPlaybackRate(
        onAdjustPlaybackRate: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        useCapture = false
    ) {
        return this.defaultKeyBinder.bindAdjustPlaybackRate(onAdjustPlaybackRate, disabledGetter, useCapture);
    }

    bindToggleSubtitles(
        onToggleSubtitles: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleSubtitles(onToggleSubtitles, disabledGetter, useCapture);
    }

    bindToggleSubtitleStudy(
        onToggleSubtitleStudy: (event: ShortcutEvent, mode: 'blur' | 'hide') => void,
        disabledGetter: () => boolean,
        useCapture?: boolean
    ): () => void {
        return this.defaultKeyBinder.bindToggleSubtitleStudy(onToggleSubtitleStudy, disabledGetter, useCapture);
    }

    bindToggleSubtitleTrackInVideo(
        onToggleSubtitleTrack: (event: ShortcutEvent, extra: any) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleSubtitleTrackInVideo(onToggleSubtitleTrack, disabledGetter, useCapture);
    }

    bindToggleSubtitleTrackInList(
        onToggleSubtitleTrackInList: (event: ShortcutEvent, extra: any) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleSubtitleTrackInList(
            onToggleSubtitleTrackInList,
            disabledGetter,
            useCapture
        );
    }

    bindUnblurTrack(
        onUnblurTrack: (event: ShortcutEvent, track: number) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindUnblurTrack(onUnblurTrack, disabledGetter, useCapture);
    }

    bindOpenStatistics(
        onOpenStatistics: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindOpenStatistics(onOpenStatistics, disabledGetter, useCapture);
    }

    bindMarkHoveredToken(
        onMarkHoveredToken: (event: ShortcutEvent, tokenStatus: TokenStatus) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindMarkHoveredToken(onMarkHoveredToken, disabledGetter, useCapture);
    }

    bindToggleHoveredTokenIgnored(
        onToggleHoveredTokenIgnored: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleHoveredTokenIgnored(
            onToggleHoveredTokenIgnored,
            disabledGetter,
            useCapture
        );
    }

    bindJumpToToken(
        onJumpToToken: (event: ShortcutEvent, target: TokenJumpTarget, forward: boolean) => boolean,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindJumpToToken(onJumpToToken, disabledGetter, useCapture);
    }

    bindPlay(
        onPlay: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindPlay(onPlay, disabledGetter, useCapture);
    }

    bindAutoPause(
        onAutoPause: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindAutoPause(onAutoPause, disabledGetter, useCapture);
    }

    bindCondensedPlayback(
        onCondensedPlayback: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindCondensedPlayback(onCondensedPlayback, disabledGetter, useCapture);
    }

    bindFastForwardPlayback(
        onFastForwardPlayback: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindFastForwardPlayback(onFastForwardPlayback, disabledGetter, useCapture);
    }

    bindToggleSidePanel(
        onToggleSidePanel: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleSidePanel(onToggleSidePanel, disabledGetter, useCapture);
    }

    bindToggleRepeat(
        onToggleRepeat: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleRepeat(onToggleRepeat, disabledGetter, useCapture);
    }

    bindCycleAutoPauseResumeMode(
        onCycleAutoPauseResumeMode: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindCycleAutoPauseResumeMode(
            onCycleAutoPauseResumeMode,
            disabledGetter,
            useCapture
        );
    }

    bindToggleSubtitleVisibility(
        onToggleSubtitleVisibility: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindToggleSubtitleVisibility(
            onToggleSubtitleVisibility,
            disabledGetter,
            useCapture
        );
    }

    bindAdjustSubtitlePositionOffset(
        onAdjustSubtitlePositionOffset: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindAdjustSubtitlePositionOffset(
            onAdjustSubtitlePositionOffset,
            disabledGetter,
            useCapture
        );
    }

    bindAdjustTopSubtitlePositionOffset(
        onAdjustTopSubtitlePositionOffset: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        useCapture?: boolean | undefined
    ): () => void {
        return this.defaultKeyBinder.bindAdjustTopSubtitlePositionOffset(
            onAdjustTopSubtitlePositionOffset,
            disabledGetter,
            useCapture
        );
    }

    unsubscribeExtension() {
        this._unsubscribeExtension?.();
    }
}
